import { ja } from '@/i18n/ja';
import type { EraChoice, UnresolvedCandidate } from '@/lib/case-number/types';
import { Button } from './ui/Button';

interface Props {
  candidates: UnresolvedCandidate[];
  onSelectEra: (candidate: UnresolvedCandidate, choice: EraChoice) => void;
}

/**
 * 「確認が必要な事件番号」。
 *
 * 事件番号としては認識できているので、システムエラーではなく
 * 「ユーザーの確認で解決できる状態」として穏やかに見せる。
 * 改元年（1912 / 1926 / 1989 / 2019）のときだけ元号を選ぶボタンを出し、
 * 選択後はそのまま変換結果へ移す（再変換は不要）。
 */
export function PendingConfirmation({ candidates, onSelectEra }: Props) {
  if (candidates.length === 0) return null;

  return (
    <section
      aria-labelledby="pending-heading"
      className="mt-6 rounded-ln border border-[var(--ln-warning-border)] bg-[var(--ln-warning-bg)]"
    >
      <div className="border-b border-[var(--ln-warning-border)] px-4 py-3">
        <h3 id="pending-heading" className="text-[15px] font-bold text-[var(--ln-warning-ink)]">
          {ja.results.pendingHeading(candidates.length)}
        </h3>
        <p className="mt-0.5 text-[13px] text-[var(--ln-warning-ink)]">{ja.results.pendingNote}</p>
      </div>

      <ul className="flex flex-col gap-4 px-4 py-4">
        {candidates.map((candidate, i) => {
          const pending = candidate.pending_era_selection;
          return (
            <li
              key={`${candidate.original_fragment}-${candidate.reason_code}-${i}`}
              className="rounded-ln border border-[var(--ln-warning-border)] bg-white p-4"
            >
              <p className="text-[13px] font-bold text-[var(--ln-warning-ink)]">
                <span aria-hidden="true" className="mr-1">
                  ⚠
                </span>
                {pending ? ja.results.pendingEraTitle : ja.results.pendingInfoTitle}
              </p>

              <p className="ln-selectable mt-1.5 break-all text-[19px] font-bold leading-snug tracking-wide text-ink">
                {pending ? pending.display : candidate.original_fragment}
              </p>

              <p className="mt-1 text-[13px] text-ink-muted">{candidate.reason}</p>

              {pending && pending.choices.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {pending.choices.map((choice) => (
                    <Button
                      key={choice.normalized}
                      variant="secondary"
                      className="border-brand-border bg-brand-subtle font-bold text-brand hover:bg-white"
                      onClick={() => onSelectEra(candidate, choice)}
                      aria-label={ja.results.pendingChoiceAriaLabel(
                        choice.label,
                        choice.normalized,
                      )}
                    >
                      {choice.label}
                    </Button>
                  ))}
                </div>
              )}

              {pending && pending.occurrence_count > 1 && (
                <p className="mt-2 text-[13px] text-ink-subtle">
                  入力中に{pending.occurrence_count}回出現
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
